import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { ChangeEvent, FC, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCollectEnabled } from "redux/slices/session";
import { EllipsisSX } from "theme";
import { P } from "theme/typography";

interface ExportChronologyModalProps {
  caseId: number;
  open: boolean;
  claimant: string;
  close: () => void;
  doExport: (
    caseId: number,
    shareToCollect: boolean,
    pushToTransfer: boolean,
    customTransferFolder?: string
  ) => Promise<unknown>;
}

interface FakeRowProps {
  date: string;
  profile: string;
  event: string;
  recordType: string;
  provider: string;
  notes: string;
  references: string;
}

const FakeRow: FC<FakeRowProps> = ({ date, profile, event, recordType, provider, notes, references }) => {
  return (
    <TableRow>
      <TableCell sx={EllipsisSX}>{date}</TableCell>
      <TableCell sx={EllipsisSX}>{profile}</TableCell>
      <TableCell sx={EllipsisSX}>{event}</TableCell>
      <TableCell sx={EllipsisSX}>{recordType}</TableCell>
      <TableCell sx={EllipsisSX}>{provider}</TableCell>
      <TableCell sx={EllipsisSX}>{notes}</TableCell>
      <TableCell sx={EllipsisSX}>{references}</TableCell>
    </TableRow>
  );
};

const ExportChronologyModal: FC<ExportChronologyModalProps> = ({ caseId, open, close, doExport }) => {
  const [shareToCollect, setShareToCollect] = useState(false);
  const [pushToTransfer, setPushToTransfer] = useState(false);
  const [customTransferFolder, setCustomTransferFolder] = useState<string>("");
  const collectEnabled = useAppSelector(selectCollectEnabled);

  const onExport = async () => {
    try {
      await doExport(caseId, shareToCollect, pushToTransfer, customTransferFolder);
    } finally {
      close();
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Chronology Export</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
            mb: 1,
          }}>
          <FormControlLabel
            disabled={!collectEnabled}
            label="Share to Collect"
            control={<Checkbox checked={shareToCollect} onChange={(evt) => setShareToCollect(evt.target.checked)} />}
          />
          <FormControlLabel
            label="Send to SFTP"
            control={<Checkbox checked={pushToTransfer} onChange={(evt) => setPushToTransfer(evt.target.checked)} />}
          />
          {pushToTransfer && (
            <TextField
              sx={{
                my: 1,
              }}
              id="custom-transfer-folder"
              label="Custom Transfer Folder"
              value={customTransferFolder}
              onChange={(event: ChangeEvent<any>) => {
                setCustomTransferFolder(event.target.value);
              }}
            />
          )}
        </Box>

        <P variant="subtitle1" sx={{ color: "grey.400" }}>
          Example Report
        </P>
        <TableContainer sx={{ position: "relative" }}>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              "&::after": {
                position: "absolute",
                content: "''",
                left: 0,
                top: 0,
                height: "100%",
                width: "100%",
                background: `linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(243, 246, 250, 0) 100%)`,
                pointerEvents: "none",
              },
            }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Profile</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Record Type</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>References</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <FakeRow
                date="10/5/2014"
                profile="Proof of Use"
                event="Remarks: Noticed ABC"
                recordType="Pharmacy"
                provider="Dr. Smith"
                notes=""
                references="Sample Document Pg. 5"
              />
              <FakeRow
                date="1/5/2018"
                profile="Injury"
                event="Diagnosis: Injury occurred"
                recordType="REF: Reference"
                provider="Dr. Francis"
                notes="Injured in activity"
                references="Sample Document Pg.101"
              />
              <FakeRow
                date="2/1/2018"
                profile="Smoking"
                event="Smoking usages found"
                recordType="REF: Reference"
                provider="Dr. Marie"
                notes=""
                references="Sample Document Pg. 210"
              />
              <FakeRow
                date="11/6/2018"
                profile="Proof of Use"
                event="Remarks: Noticed ABC"
                recordType="Pharmacy"
                provider="Dr. Smith"
                notes=""
                references="Sample Document A Pg. 5"
              />
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="default">
          Cancel
        </Button>
        <Button onClick={onExport} color="default" variant="outlined">
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportChronologyModal;
